import classNames from 'clsx'
import { ComponentProps } from 'react'
import { twMerge } from 'tailwind-merge'
import { useFormContext, useController } from 'react-hook-form'

import type { Option } from 'types/global'

import { Icon } from 'components'
import { MessageError } from 'components/Form'

interface Props extends ComponentProps<'select'> {
  placeholder?: string
  label?: string
  options: Option[]
}

export const Select = ({ name, className = '', options, ...rest }: Props) => {
  const { control } = useFormContext()
  const {
    field: { value, ...field },
    fieldState
  } = useController({ name, control, defaultValue: '' })

  const messageError = fieldState.error?.message
  const isError = typeof messageError === 'string'

  return (
    <fieldset
      className={twMerge('flex flex-col gap-1 w-full items-start', className)}
    >
      {rest?.label && (
        <label
          htmlFor={name}
          className={classNames(
            'text-body2 font-regular',
            isError && 'text-error-500',
            rest?.disabled && 'text-font-color-disabled'
          )}
        >
          {rest.label}
          {rest?.required && <span className="text-error-500">*</span>}
        </label>
      )}
      <div className="relative w-full h-10 flex">
        <select
          {...rest}
          {...field}
          name={name}
          value={value}
          className={classNames(
            'appearance-none w-full bg-white rounded-lg border border-gray-500/[.32] hover:border-gray-800 pl-4 pr-8 outline-0.5 shadow-outline outline-primary-700 focus:text-gray-800 disabled:cursor-not-allowed disabled:hover:border-gray-300 disabled:text-font-color-disabled h-10',
            isError && 'border-2 border-error-500 outline-transparent',
            !value && 'text-gray-500'
          )}
        >
          <option value="" className="text-gray-500">
            {rest?.placeholder || 'Selecione'}
          </option>
          {options.map(option => (
            <option value={option.value} key={option.value}>
              {option.text}
            </option>
          ))}
        </select>

        <Icon
          face="keyboard_arrow_down"
          size="md"
          className="absolute top-0 bottom-0 right-2 self-center text-gray-600"
        />
      </div>
      <MessageError className="mt-1">{messageError}</MessageError>
    </fieldset>
  )
}
